/* External */
import { useMemo } from 'react';

/* Utils */
import {
  getFirstGenre,
  getAllGenres,
  getFormattedDuration,
  getFullEpisodeName,
  useFullEpisodeName,
  getRating,
  isEpisode,
  isEvent,
  isMovie,
  isSports,
  joinArrayWithSeparator,
  joinArrayWithSpace,
} from '../../../utils/metaDataUtils';
import { DOT_SEPARATOR } from '../../../utils/constants';

/* The loader's content-type badge (old LoaderScreenV2 badgeTypes). Exported so
   the lego player shell resolves the same message ids instead of keeping a
   second copy that can drift. */
export const PLAYER_LOADER_BADGE_IDS = {
  EPISODE: 'player.badge.episode',
  MOVIE: 'player.badge.movie',
  SHOW: 'player.badge.show',
  SPORTS: 'player.badge.sports',
  SPORT: 'player.badge.sports',
};

export function getPlayerLoaderBadgeLabel(content, formatMessage) {
  const id = PLAYER_LOADER_BADGE_IDS[content?.type] || '';
  return id && formatMessage ? formatMessage({ id }) : '';
}

/* Pure core shared by the hook below and the lego shell's loader metadata
   builder (which has no React tree to read the memoized hook from). Must
   return a plain STRING: the lego shell hands it to Solid, which rendered a
   joinArrayWithSpace() Fragment array as "[object Object]". */
export function resolvePlayerLoaderSubtitle(content, fullEpisodeName) {
  if (isEpisode(content)) {
    if (!content?.episode || content.title === content?.episode?.showName) {
      return '';
    }

    return fullEpisodeName || '';
  }

  return '';
}

export function getPlayerLoaderSubtitle(content, formatMessage) {
  return resolvePlayerLoaderSubtitle(
    content,
    getFullEpisodeName(content, { formatMessage }),
  );
}

export function usePlayerLoaderSubtitle(content) {
  const fullEpisodeName = useFullEpisodeName(content);

  return useMemo(
    () => resolvePlayerLoaderSubtitle(content, fullEpisodeName),
    [content, fullEpisodeName],
  );
}

/* The loader's second line is a rating badge plus a text tail. Split into parts
   so the lego player (Solid) can render the badge itself: a React node cannot
   cross the mount contract. `rating` is undefined when the legacy branch
   omitted it (live sports replace it with shortName • duration). */
export function getPlayerLoaderSecondSubtitleParts({ content, schedule }) {
  if (isEpisode(content) || isMovie(content)) {
    return { rating: content?.rating, text: getAllGenres(content, DOT_SEPARATOR) };
  }

  if (!!schedule && isSports(content) && !isEvent(content)) {
    return {
      rating: undefined,
      text: joinArrayWithSeparator([
        content?.shortName,
        getFormattedDuration(content),
      ], DOT_SEPARATOR),
    };
  }

  return {
    rating: content?.rating,
    text: joinArrayWithSeparator([
      getFirstGenre(content),
      isMovie(content) && content?.releaseYear,
      !schedule && getFormattedDuration(content),
    ], DOT_SEPARATOR),
  };
}

export function getPlayerLoaderSecondSubtitle({
  content, schedule,
}) {
  const { rating, text } = getPlayerLoaderSecondSubtitleParts({ content, schedule });

  return joinArrayWithSpace([rating && getRating({ rating }), text]);
}

export function usePlayerSubtitle(content) {
  const fullEpisodeName = useFullEpisodeName(content);

  return useMemo(() => {
    if (isEpisode(content)) {
      return joinArrayWithSpace([fullEpisodeName.replace(DOT_SEPARATOR, ' | ')]);
    }

    if (isSports(content) && !isEvent(content) && content?.shortName) {
      return content.shortName;
    }

    return joinArrayWithSpace([
      getRating(content),
      getAllGenres(content, DOT_SEPARATOR),
    ]);
  }, [content, fullEpisodeName]);
}

export function getPlayerSecondSubtitle({ content }) {
  if (isEpisode(content)) {
    return joinArrayWithSpace([
      getRating(content),
      getAllGenres(content, DOT_SEPARATOR),
    ]);
  }

  if (isSports(content) && !isEvent(content) && content?.shortName) {
    return joinArrayWithSpace([
      getRating(content),
      getAllGenres(content, DOT_SEPARATOR),
    ]);
  }

  return '';
}
